import { values } from "lodash";
import { FORM_SCHEMA_TYPES as TYPES } from "../formSchemaTypes";

const CANADA_SELECT_VALUES = {
  COUNTRIES_V2: "COUNTRIES_V2",
  MARITAL_STATUS: "MARITAL_STATUS",
  GENDER: "GENDER",
  PASSPORT_TYPE: "PASSPORT_TYPE",
  OCCUPATION: "OCCUPATION",
  SECURITY_QUESTIONS: "SECURITY_QUESTIONS",
};

export const canadaSchema = {
  generalInfoSegment: {
    label: "General application information",
    type: TYPES.SEGMENT,
    initialOpen: true,
    data: {
      _id: {
        label: "ID",
      },
      isFullyFilled: {
        label: "Form fully filled",
      },
      applicantNumber: {
        label: "Applicant number",
        type: TYPES.TEXT,
      },
      siteId: {
        label: "Referral Site",
      },
      orderId: {
        label: "Order ID",
      },
      orderIdHistory: {
        label: "Order ID History",
      },
      invoiceEmail: {
        label: "Payment email",
      },
      zendeskId: {
        label: "Zendesk ID",
        type: TYPES.TEXT,
        required: false,
      },
      userLanguage: {
        label: "User form language",
      },
      userCountry: {
        label: "Users IP country",
      },
      botRuns: {
        label: "Number of bot runs",
      },
      userEmail: {
        label: "User contact email",
        type: TYPES.EMAIL,
      },
      submissionEmail: {
        label: "User submission email",
        type: TYPES.TEXT,
      },
    },
  },
  applicant_information: {
    label: "Applicant Information",
    type: TYPES.SEGMENT,
    data: {
      firstName: {
        label: "First Name",
        type: TYPES.TEXT,
        required: true,
      },
      lastName: {
        label: "Last Name",
        type: TYPES.TEXT,
        required: true,
      },
      userEmail: {
        label: "Email",
        type: TYPES.EMAIL,
        required: true,
      },
      birthDate: {
        label: "Date of Birth",
        type: TYPES.DATE,
        required: true,
      },
      birthCountry: {
        label: "Country of Birth",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.COUNTRIES_V2,
      },
      birthCity: {
        label: "Place of birth",
        type: TYPES.TEXT,
        required: true,
      },
      maritalStatus: {
        label: "Marital Status",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.MARITAL_STATUS,
      },
      phoneNumber: {
        label: "Phone Number",
        type: TYPES.TEXT,
        required: true,
      },
      gender: {
        label: "Gender",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.GENDER,
      },
      knowTravelDates: {
        label: "Do you know when you will travel to Canada?",
        type: TYPES.CHECKBOX,
      },
      travelPlanDetails: {
        label: "Travel plan details",
        type: TYPES.NESTED,
        dependsOn: "knowTravelDates",
        newTable: true,
        nested: {
          arrivalDate: {
            label: "Intended date of arrival",
            type: TYPES.DATE,
            dependsOn: "knowTravelDates",
            path: "travelPlanDetails",
          },
          flightDepartureTime: {
            label: "Please enter the time your flight to Canada will depart",
            type: TYPES.TIME,
            dependsOn: "knowTravelDates",
            path: "travelPlanDetails",
          },
        },
      },
      addressLine1: {
        label: "Address Line 1",
        type: TYPES.TEXT,
        required: true,
      },
      addressLine2: {
        label: "Address Line 2",
        type: TYPES.TEXT,
      },
      addressCountry: {
        label: "Country",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.COUNTRIES_V2,
      },
      addressCity: {
        label: "City",
        type: TYPES.TEXT,
        required: true,
      },
      addressPostalCode: {
        label: "Postal Code",
        type: TYPES.TEXT,
        required: true,
      },
    },
  },
  passport_information: {
    label: "Passport Information",
    type: TYPES.SEGMENT,
    data: {
      applicantCountry: {
        label: "Applicant country",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.COUNTRIES_V2,
      },
      nationality: {
        label: "Current nationality",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.COUNTRIES_V2,
      },
      passportType: {
        label: "Passport type",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.PASSPORT_TYPE,
      },
      passportNumber: {
        label: "Passport number",
        type: TYPES.TEXT,
        required: true,
      },
      dateOfIssue: {
        label: "Date of issue",
        type: TYPES.DATE,
        required: true,
      },
      passportExpiryDate: {
        label: "Expiration date",
        type: TYPES.DATE,
        required: true,
      },
      hasPRTD: {
        label:
          "Do you have a permanent resident card or permanent resident travel document (PRTD)?",
        type: TYPES.CHECKBOX,
      },
      citizenOfAdditionalNationalities: {
        label: "Are you a citizen of any additional nationalities?",
        type: TYPES.CHECKBOX,
      },
      additionalNationalities: {
        label: "Other nationality",
        type: TYPES.MULTI_SELECT,
        values: CANADA_SELECT_VALUES.COUNTRIES_V2,
        dependsOn: "citizenOfAdditionalNationalities",
      },
    },
  },
  work_education_information: {
    label: "Work and Education Information",
    type: TYPES.SEGMENT,
    data: {
      jobOccupation: {
        label: "Primary occupation",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.OCCUPATION,
      },
      occupationDetails: {
        label: "Occupation details",
        type: TYPES.NESTED,
        newTable: true,
        nested: {
          schoolName: {
            label: "School name",
            type: TYPES.TEXT,
            dependsOn: "jobOccupation",
            dependantOnValues: ["Student"],
            path: "occupationDetails",
          },
          schoolYear: {
            label: "Current year of study",
            type: TYPES.TEXT,
            dependsOn: "jobOccupation",
            dependantOnValues: ["Student"],
            path: "occupationDetails",
          },
          schoolCountry: {
            label: "School country",
            type: TYPES.SELECT,
            dependsOn: "jobOccupation",
            dependantOnValues: ["Student"],
            path: "occupationDetails",
            values: CANADA_SELECT_VALUES.COUNTRIES_V2,
          },
          schoolCity: {
            label: "School city",
            type: TYPES.TEXT,
            dependsOn: "jobOccupation",
            dependantOnValues: ["Student"],
            path: "occupationDetails",
          },
          jobTitle: {
            label: "Job title",
            type: TYPES.TEXT,
            dependsOn: "jobOccupation",
            path: "occupationDetails",
            dependantOnValues: [
              "Agriculture",
              "Artist / performer",
              "Business",
              "Communications",
              "Computer science",
              "Culinary / food services",
              "Education",
              "Engineering",
              "Government",
              "Legal profession",
              "Medical / health",
              "Military",
              "Natural science",
              "Physical science",
              "Religious vocation",
              "Research",
              "Social science",
            ],
          },
          jobEmployerName: {
            label: "Employer name",
            type: TYPES.TEXT,
            path: "occupationDetails",
            dependsOn: "jobOccupation",
            dependantOnValues: [
              "Agriculture",
              "Artist / performer",
              "Business",
              "Communications",
              "Computer science",
              "Culinary / food services",
              "Education",
              "Engineering",
              "Government",
              "Legal profession",
              "Medical / health",
              "Military",
              "Natural science",
              "Physical science",
              "Religious vocation",
              "Research",
              "Social science",
            ],
          },
          jobCountry: {
            label: "Job country",
            type: TYPES.SELECT,
            path: "occupationDetails",
            values: CANADA_SELECT_VALUES.COUNTRIES_V2,
            dependsOn: "jobOccupation",
            dependantOnValues: [
              "Agriculture",
              "Artist / performer",
              "Business",
              "Communications",
              "Computer science",
              "Culinary / food services",
              "Education",
              "Engineering",
              "Government",
              "Legal profession",
              "Medical / health",
              "Military",
              "Natural science",
              "Physical science",
              "Religious vocation",
              "Research",
              "Social science",
            ],
          },
          jobCity: {
            label: "Job city",
            type: TYPES.TEXT,
            path: "occupationDetails",
            dependsOn: "jobOccupation",
            dependantOnValues: [
              "Agriculture",
              "Artist / performer",
              "Business",
              "Communications",
              "Computer science",
              "Culinary / food services",
              "Education",
              "Engineering",
              "Government",
              "Legal profession",
              "Medical / health",
              "Military",
              "Natural science",
              "Physical science",
              "Religious vocation",
              "Research",
              "Social science",
            ],
          },
          jobYear: {
            label: "Since what year?",
            type: TYPES.TEXT,
            path: "occupationDetails",
            dependsOn: "jobOccupation",
            dependantOnValues: [
              "Agriculture",
              "Artist / performer",
              "Business",
              "Communications",
              "Computer science",
              "Culinary / food services",
              "Education",
              "Engineering",
              "Government",
              "Legal profession",
              "Medical / health",
              "Military",
              "Natural science",
              "Physical science",
              "Religious vocation",
              "Research",
              "Social science",
            ],
          },
        },
      },
    },
  },
  background_questions: {
    label: "Background questions",
    type: TYPES.SEGMENT,
    data: {
      backgroundRefused: {
        label:
          "Have you ever been refused a visa or permit, denied entry, or ordered to leave any country/territory?",
        type: TYPES.CHECKBOX,
      },
      backgroundRefusedDescribe: {
        label:
          "For each refusal, please specify the country and the reason given",
        type: TYPES.TEXT,
        dependsOn: "backgroundRefused",
      },
      backgroundCrimes: {
        label: "Have you ever been arrested or convicted of a crime?",
        type: TYPES.CHECKBOX,
      },
      backgroundCrimesDescribe: {
        label:
          "Have you ever been involved in any criminal offense in any country/territory?",
        type: TYPES.TEXT,
        dependsOn: "backgroundCrimes",
      },
      hasTuberculosis: {
        label:
          "In the past two years, have you had tuberculosis or been in close contact with someone who has it?",
        type: TYPES.CHECKBOX,
      },
      hasPsychosis: {
        label:
          "Do you have an untreated mental health condition with psychosis?",
        type: TYPES.CHECKBOX,
      },
      hasSyphilis: {
        label: "Have you been diagnosed with untreated syphilis?",
        type: TYPES.CHECKBOX,
      },
      hasDrugAddiction: {
        label: "Do you have an untreated drug addiction?",
        type: TYPES.CHECKBOX,
      },
      hasAlcoholAddiction: {
        label: "Do you have an untreated alcohol addiction?",
        type: TYPES.CHECKBOX,
      },
    },
  },
  security_questions: {
    label: "Consent and declaration",
    type: TYPES.SEGMENT,
    data: {
      securityQuestion: {
        label: "Security question",
        type: TYPES.SELECT,
        required: true,
        values: CANADA_SELECT_VALUES.SECURITY_QUESTIONS,
      },
      securityAnswer: {
        label: "Answer",
        type: TYPES.TEXT,
        required: true,
      },
      signature: {
        label: "Signature of applicant",
        type: TYPES.TEXT,
        required: true,
      },
      consentDeclaration: {
        label: "Agree with Consent and Declaration",
        type: TYPES.CHECKBOX,
        required: true,
      },
      termsConditions: {
        label: "Agree with Terms & Conditions",
        type: TYPES.CHECKBOX,
        required: true,
      },
      parental_consent: {
        label: "Parental Consent (if applicable)",
        type: TYPES.NESTED,
        newTable: true,
        nested: {
          parentGuardianName: {
            label: "Parent/Guardian Name",
            type: TYPES.TEXT,
          },
          parentGuardianSignature: {
            label: "Signature of parent or guardian",
            type: TYPES.TEXT,
          },
        },
      },
    },
  },
};
